import { Button } from "components/Button"
import { Image } from "components/Image"
import { Link } from "components/Link"
import { Text } from "components/Text"
import { useTranslator } from "components/Translator"
import { MaybeWrapper, Wrapper } from "components/Wrapper"
import * as R from "remeda"
import { useConnectedUser } from "components/ConnectedUserProvider"
import * as User from "domains/user/model"
import * as UserType from "enums/UserType"
import { Carousel, useCarousel } from "components/Carousel"
import quoteOpenImage from "img/quote-open.png"
import classNames from "classnames"
import { formatDistanceStrict } from "utils/date"
import { useAdvertSuggestions } from "domains/advert/suggestions"
import * as LandingTargetType from "enums/LandingTargetType"
import { AdvertResult } from "components/SearchResult"
import { AdvertInteractionsProvider } from "domains/advert/AdvertInteractionsProvider"
import { ArrowRight } from "components/Icon"
import { Metrics } from "components/Metrics"
import { useEffect, useRef, useState } from "react"
import { useScrollMargins } from "hooks/useScrollMargins"
import { useBreakpoints } from "hooks/useBreakpoints"
import { Loader } from "components/Loader"
import { useRouter } from "components/Router"

export function ClubHomeLandingPage({
  landing,
  testimonies,
  metrics,
  navigationType,
}) {
  const anchors = getAnchors(landing.content)
  const searchParams = new URLSearchParams(window.location.search)
  const term = searchParams.get("term")

  const { lg } = useBreakpoints()

  return (
    <div>
      <div className={"pt-6 grid gap-4 lg:gap-8 lg:px-18"}>
        <Wrapper>
          <Text variant={"headline2"} tag={"h1"} className={"text-dark"}>
            {landing.title}
          </Text>
        </Wrapper>

        <MaybeWrapper condition={lg}>
          <div className={"grid gap-4 lg:grid-cols-3 lg:gap-9"}>
            <div className={"pb-12 lg:col-span-2"}>
              {landing.imageFile ? (
                <Image imageFile={landing.imageFile} size={"w1920"} />
              ) : null}

              <MaybeWrapper condition={!lg} className={"mt-3"}>
                <div className={"grid gap-2 lg:grid-cols-2 lg:gap-x-4"}>
                  {anchors.map(({ id, content }) => (
                    <Link key={id} href={`#${id}`}>
                      {content}
                    </Link>
                  ))}
                </div>
              </MaybeWrapper>

              <div className={"mt-12"}>
                <CatchItems items={landing.catchItems} />
              </div>
            </div>

            <Testimonies testimonies={testimonies} />
          </div>
        </MaybeWrapper>
      </div>

      <Suggestions navigationType={navigationType} term={term} />

      <Content content={landing.content} />

      <Metrics metrics={metrics} color={"secondary"} />

      <Quote quote={landing.quote} />
    </div>
  )
}

function getAnchors(html) {
  const container = document.createElement("div")
  container.innerHTML = html

  return Array.from(container.querySelectorAll("h2")).map((element) => ({
    id: element.id,
    content: element.textContent,
  }))
}
function CatchItems({ items }) {
  const { locale } = useTranslator()
  const capitalizedLocale = R.capitalize(locale)

  return (
    <ol className={"grid gap-7 lg:grid-cols-3 lg:gap-18"}>
      {items.map((item, index) => {
        const title = item[`title${capitalizedLocale}`]

        return <CatchItem key={title} index={index + 1} title={title} />
      })}
    </ol>
  )
}

function CatchItem({ index, title }) {
  return (
    <li className={"flex flex-col items-center gap-2.5"}>
      <div
        className={"size-14 bg-grey-light rounded-full grid place-items-center"}
      >
        <div
          className={
            "size-11 bg-primary-default rounded-full grid place-items-center text-lg font-bold text-grey-light"
          }
        >
          {index}
        </div>
      </div>

      <Text
        variant={"headline5"}
        className={"text-primary-default text-center"}
      >
        {title}
      </Text>
    </li>
  )
}

function Testimonies({ testimonies }) {
  const translator = useTranslator()
  const translationKey = useTranslationKey()

  const { active, handlers, style, setActive } = useCarousel({
    length: testimonies.length,
  })

  const { lg } = useBreakpoints()

  const ctaHref = useCTAHref()

  return (
    <section aria-labelledby={"testimonies-title"}>
      <div className={"bg-primary-light pt-7 pb-11 lg:rounded-lg"}>
        <MaybeWrapper condition={!lg}>
          <div className={"lg:px-10"}>
            <Text
              variant={"subtitle1"}
              tag={"h2"}
              id={"testimonies-title"}
              className={"text-primary-default text-center"}
            >
              {translator.trans(
                "club.home.landing.testimonies.title",
                null,
                "pages",
              )}
            </Text>

            <div className={"mt-7"}>
              <img src={quoteOpenImage} alt={""} />
            </div>
          </div>
        </MaybeWrapper>

        <div className={"overflow-clip mt-9"}>
          <div
            className={"grid relative"}
            {...(testimonies.length > 1 ? handlers : {})}
            style={{
              ...(testimonies.length > 1 ? style : {}),
              gridTemplateColumns:
                testimonies.length > 1
                  ? `repeat(${testimonies.length + 2}, 1fr)`
                  : "1fr",
            }}
          >
            {(testimonies.length > 1
              ? [
                  testimonies[testimonies.length - 1],
                  ...testimonies,
                  testimonies[0],
                ]
              : testimonies
            ).map((testimony, index) => (
              <div
                key={`${testimony.id}${index === 0 || index === testimonies.length - 1 ? "duplicate" : ""}`}
                className={"min-w-0"}
              >
                <TestimonySlide testimony={testimony} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={"bg-grey-lighter py-7 flex flex-col items-center gap-5"}>
        <ul className={"flex gap-3 justify-center flex-wrap"}>
          {testimonies.map((testimony, index) => (
            <li key={testimony.id}>
              <button
                type={"button"}
                onClick={() => setActive(index)}
                className={classNames("size-3 shrink-0 rounded-full", {
                  "bg-grey-light": active !== index,
                  "bg-primary-default": active === index,
                })}
              ></button>
            </li>
          ))}
        </ul>

        <Button href={ctaHref}>
          {translator.trans(
            `club.home.landing.testimonies.cta.${translationKey}`,
          )}
        </Button>
      </div>
    </section>
  )
}

function useTranslationKey() {
  const connectedUser = useConnectedUser()

  if (!connectedUser) return "anonymous"

  if (User.isClub(connectedUser)) {
    if (connectedUser.type.enum_value === UserType.OWNER) {
      return "club.owner"
    } else {
      return "club.crew"
    }
  }

  if (User.isExClub(connectedUser)) {
    if (connectedUser.type.enum_value === UserType.OWNER) {
      return "exClub.owner"
    } else {
      return "exClub.crew"
    }
  }

  if (connectedUser.type.enum_value === UserType.OWNER) {
    return "free.owner"
  }

  return "free.crew"
}

function useCTAHref() {
  const connectedUser = useConnectedUser()
  const router = useRouter()

  if (!connectedUser) {
    return router.generate("app_user_security_register")
  }

  if (User.isClub(connectedUser)) {
    return router.generate("app_user_advert_add")
  }

  if (User.isExClub(connectedUser)) {
    return router.generate("app_club_shop_plan_show", { planId: 8 })
  }

  return router.generate("app_club_shop_plan_index")
}

function TestimonySlide({ testimony }) {
  const translator = useTranslator()
  const { lg } = useBreakpoints()
  const contentRef = useRef()
  const hasMoreContent = useHasMoreContent(contentRef)
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <MaybeWrapper condition={!lg}>
      <div className={"lg:px-10 text-dark grid gap-9 lg:gap-14"}>
        <div className={"grid gap-2"}>
          <Text
            className={classNames({ "line-clamp-10": !isExpanded })}
            ref={contentRef}
          >
            {testimony.content}
          </Text>

          {hasMoreContent ? (
            <Text
              tag={"button"}
              type={"button"}
              className={"text-left font-semibold text-primary-darker"}
              variant={"caption"}
              onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
            >
              Voir {isExpanded ? "moins" : "plus"}
            </Text>
          ) : null}
        </div>

        <div>
          <Text variant={"body1"} className={"font-bold"}>
            {testimony.user.fullName}
          </Text>

          <Text variant={"body2"}>
            {translator.trans(
              "club.home.landing.testimonies.user.since",
              {
                duration: formatDistanceStrict(
                  new Date(testimony.user.createdAt),
                  new Date(),
                ),
              },
              "pages",
            )}
          </Text>
        </div>
      </div>
    </MaybeWrapper>
  )
}

function useHasMoreContent(ref) {
  const [hasMoreContent, setHasMoreContent] = useState(false)

  useEffect(() => {
    setHasMoreContent(ref.current.scrollHeight > ref.current.clientHeight)
  }, [ref])

  return hasMoreContent
}

function Suggestions({ navigationType, term }) {
  const translator = useTranslator()

  const suggestionsRequest = useAdvertSuggestions({
    navigationType,
    from: "landing",
  })

  const translationKey = useTranslationKey()

  const ctaHref = useCTAHref()

  return (
    <section
      aria-labelledby={"suggestions-title"}
      className={"bg-white pt-5 pb-9 lg:py-11 grid"}
    >
      <div className={"grid gap-7 lg:px-[123px]"}>
        <Wrapper>
          <Text variant={"headline5"} tag={"h2"} id={"suggestions-title"}>
            {translator.trans(
              "club.home.landing.suggestions.title",
              null,
              "pages",
            )}
          </Text>
        </Wrapper>

        {suggestionsRequest.status === "loading" ? (
          <div className={"flex justicy-center"}>
            <Loader />
          </div>
        ) : null}

        {suggestionsRequest.status === "error" ? (
          <Text>
            {translator.trans(
              "club.home.landing.suggestions.error",
              null,
              "pages",
            )}
          </Text>
        ) : null}

        {suggestionsRequest.status === "success" ? (
          <>
            <SuggestionsResults data={suggestionsRequest.value} term={term} />

            <Wrapper>
              <div className={"flex justify-end"}>
                <Button
                  href={suggestionsRequest.value.adverts.moreUrl}
                  variant={"text"}
                  color={"secondary"}
                  size={"small"}
                  iconRight={<ArrowRight className={"size-4"} />}
                >
                  {translator.trans(
                    "club.home.landing.suggestions.more",
                    null,
                    "pages",
                  )}
                </Button>
              </div>
            </Wrapper>

            <Wrapper>
              <div className={"flex justify-center"}>
                <Button href={ctaHref}>
                  {translator.trans(
                    `club.home.landing.suggestions.cta.${translationKey}`,
                    null,
                    "pages",
                  )}
                </Button>
              </div>
            </Wrapper>
          </>
        ) : null}
      </div>
    </section>
  )
}

function SuggestionsResults({ data, term }) {
  const connectedUser = useConnectedUser()

  const adverts = getAdvertsForUser(data.adverts, connectedUser, term)

  return (
    <AdvertInteractionsProvider ids={adverts.map((advert) => advert.id)}>
      <div className={"lg:hidden min-w-0"}>
        <SuggestionsResultsMobile adverts={adverts} />
      </div>

      <div className={"hidden lg:block"}>
        <SuggestionsResultsDesktop adverts={adverts} />
      </div>
    </AdvertInteractionsProvider>
  )
}

function SuggestionsResultsMobile({ adverts }) {
  return (
    <div className={"overflow-x-auto px-5"}>
      <div className={"flex gap-5"}>
        {adverts.map((advert) => (
          <div key={advert.id} className={"w-64 shrink-0"}>
            <AdvertResult key={advert.id} advert={advert} />
          </div>
        ))}
      </div>
    </div>
  )
}

function SuggestionsResultsDesktop({ adverts }) {
  const slides = R.pipe(
    adverts,
    R.chunk(3),
    R.map((adverts) => (
      <div
        key={adverts.map((advert) => advert.id).join("-")}
        className={"grid grid-cols-3 gap-6 overflow-clip"}
      >
        {adverts.map((advert) => (
          <div key={advert.id}>
            <AdvertResult advert={advert} />
          </div>
        ))}
      </div>
    )),
  )

  return (
    <Wrapper>
      <Carousel
        slides={slides}
        components={{
          ActiveSlideIndicator: () => null,
          PreviousButton: () => null,
          NextButton: () => null,
          BulletsContainer: ({ children }) => {
            if (slides.length === 1) return null

            return (
              <div className={"flex justify-center gap-3 flex-wrap mt-8"}>
                {children}
              </div>
            )
          },
          Bullet,
        }}
      />
    </Wrapper>
  )
}

const Bullet = ({ isActive, onClick }) => {
  return (
    <button
      type={"button"}
      className={classNames("rounded-full size-4 bg-grey-light", {
        "bg-primary-default": isActive,
      })}
      onClick={onClick}
    ></button>
  )
}

function getAdvertsForUser(adverts, user, term) {
  if (user) {
    if (user.type.enum_value === UserType.OWNER) {
      return adverts.items.crew ?? []
    } else {
      return adverts.items.boat ?? []
    }
  }

  if (term === LandingTargetType.CREW) {
    return [...(adverts.items.crew ?? []), ...(adverts.items.boat ?? [])]
  }

  return [...(adverts.items.boat ?? []), ...(adverts.items.crew ?? [])]
}

function Content({ content }) {
  const translator = useTranslator()
  const translationKey = useTranslationKey()

  const contentRef = useRef(null)

  const scrollMargins = useScrollMargins()

  const ctaHref = useCTAHref()

  useEffect(() => {
    const h2s = Array.from(contentRef.current?.querySelectorAll("h2") ?? [])

    for (const h2 of h2s) {
      h2.classList.add(scrollMargins)
    }
  }, [scrollMargins])

  return (
    <section className={"py-14 lg:py-18 bg-grey-lighter grid gap-14"}>
      <Wrapper>
        <div
          className={"prose max-w-none lg:px-18"}
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </Wrapper>

      <Wrapper>
        <div className={"flex justify-center"}>
          <Button href={ctaHref}>
            {translator.trans(`club.home.landing.content.${translationKey}`)}
          </Button>
        </div>
      </Wrapper>
    </section>
  )
}

function Quote({ quote }) {
  return (
    <section className={"bg-grey-lighter py-14 lg:py-16"}>
      <Wrapper>
        <blockquote
          className={"text-center text-dark text-lg max-w-[777px] mx-auto"}
        >
          {`“ ${quote} ”`}
        </blockquote>
      </Wrapper>
    </section>
  )
}
